import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import type { Task, TaskSummary } from "@/types/task";
import { TaskList } from "@/components/tasks/TaskList";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckSquare2,
  Plus,
  Search,
  RefreshCw,
  AlertCircle,
  Calendar,
  CalendarRange,
  CheckCircle2,
  Users,
  User,
  Filter,
} from "lucide-react";

export default function TasksPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Active tab: overdue | today | upcoming | completed
  const activeTab = searchParams.get("view") || "today";
  // Active scope: mine | team
  const activeScope = searchParams.get("scope") || "mine";
  // Task type filter: ALL | FOLLOW_UP | CALL | MEETING | EMAIL | WHATSAPP | OTHER
  const typeFilter = searchParams.get("type") || "ALL";
  const searchQuery = searchParams.get("search") || "";

  const [summary, setSummary] = useState<TaskSummary>({
    overdue: 0,
    today: 0,
    upcoming: 0,
    completed: 0,
    totalActive: 0,
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Team view is available to anyone whose activity data scope is wider than their own records
  // (managers: team, Sales Head / admins: all), matching what the API returns
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();
  const activityScope = user?.data_scopes?.activity;
  const isManagerOrAdmin =
    roleCode === "SUPER_ADMIN" || activityScope === "team" || activityScope === "all";

  // Calculate today's local boundaries for API
  const getTodayRange = () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return {
      from: start.toISOString(),
      to: end.toISOString(),
    };
  };

  const fetchSummary = useCallback(async () => {
    try {
      const { from, to } = getTodayRange();
      const typeParam = typeFilter !== "ALL" ? `&task_type=${typeFilter}` : "";
      const res = await api.get(
        `/api/Task/Summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(
          to
        )}&scope=${activeScope}${typeParam}`
      );
      const data = unwrapObject<TaskSummary>(res);
      setSummary(data);
    } catch (err) {
      console.warn("Could not load task summary:", err);
    }
  }, [activeScope, typeFilter]);

  const list = usePagedList<Task>({
    deps: [activeTab, activeScope, typeFilter, searchQuery],
    debounceMs: searchQuery.trim() ? 350 : 0,
    load: async (page) => {
      const { from, to } = getTodayRange();
      const params = new URLSearchParams();
      params.set("view", activeTab);
      params.set("scope", activeScope);
      params.set("from", from);
      params.set("to", to);
      params.set("page", String(page));
      params.set("page_size", "30");
      if (typeFilter !== "ALL") params.set("task_type", typeFilter);
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      try {
        return unwrapPage<Task>(await api.get(`/api/Task/List?${params.toString()}`));
      } catch (err) {
        console.warn("Could not load tasks:", err);
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const tasks = list.rows;
  const loading = list.loading;
  const fetchTasks = list.reload;

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const setViewTab = (view: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", view);
    setSearchParams(next);
  };

  const setScope = (scope: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("scope", scope);
    setSearchParams(next);
  };

  const setTypeFilter = (type: string) => {
    const next = new URLSearchParams(searchParams);
    if (type !== "ALL") {
      next.set("type", type);
    } else {
      next.delete("type");
    }
    setSearchParams(next);
  };

  const handleSearchChange = (val: string) => {
    const next = new URLSearchParams(searchParams);
    if (val.trim()) {
      next.set("search", val);
    } else {
      next.delete("search");
    }
    setSearchParams(next);
  };

  return (
    <div className="space-y-6 flex-1 flex flex-col">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <CheckSquare2 className="size-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                My Day & Follow-Up Tasks
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Organize, execute, and keep customer follow-ups on track.
              </p>
            </div>
          </div>
        </div>

        {/* Actions & Scope Toggle */}
        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Mine / Team scope switch */}
          {isManagerOrAdmin && (
            <div className="flex items-center p-1 rounded-lg border border-border bg-card">
              <button
                type="button"
                onClick={() => setScope("mine")}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  activeScope === "mine"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <User className="size-3.5" />
                <span>My Tasks</span>
              </button>
              <button
                type="button"
                onClick={() => setScope("team")}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  activeScope === "team"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Users className="size-3.5" />
                <span>Team Tasks</span>
              </button>
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchTasks();
              fetchSummary();
            }}
            title="Refresh tasks"
          >
            <RefreshCw className="size-3.5" />
          </Button>

          <Button
            asChild
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5 shadow-sm border-primary/20 hover:border-primary/40 text-foreground"
          >
            <Link to="/calendar">
              <Calendar className="size-4 text-primary" />
              <span>Calendar View</span>
            </Link>
          </Button>

          <Button
            onClick={() => setCreateDialogOpen(true)}
            size="sm"
            className="flex items-center gap-1.5 shadow-sm"
          >
            <Plus className="size-4" />
            <span>New Task</span>
          </Button>
        </div>
      </div>

      {/* Tabs & Search Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-3">
        {/* Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {/* Overdue */}
          <button
            type="button"
            onClick={() => setViewTab("overdue")}
            className={`px-3.5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
              activeTab === "overdue"
                ? "bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900 shadow-sm"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            }`}
          >
            <AlertCircle className="size-4 text-red-600 dark:text-red-400" />
            <span>Overdue</span>
            {summary.overdue > 0 && (
              <span className="text-[11px] font-bold px-1.5 py-0.2 rounded-full bg-red-600 text-white">
                {summary.overdue}
              </span>
            )}
          </button>

          {/* Today */}
          <button
            type="button"
            onClick={() => setViewTab("today")}
            className={`px-3.5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
              activeTab === "today"
                ? "bg-primary/10 text-primary border border-primary/20 shadow-sm"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            }`}
          >
            <Calendar className="size-4" />
            <span>Today</span>
            {summary.today > 0 && (
              <span className="text-[11px] font-bold px-1.5 py-0.2 rounded-full bg-primary text-primary-foreground">
                {summary.today}
              </span>
            )}
          </button>

          {/* Upcoming */}
          <button
            type="button"
            onClick={() => setViewTab("upcoming")}
            className={`px-3.5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
              activeTab === "upcoming"
                ? "bg-card text-foreground border border-border shadow-sm"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            }`}
          >
            <CalendarRange className="size-4" />
            <span>Upcoming</span>
            {summary.upcoming > 0 && (
              <span className="text-[11px] font-semibold px-1.5 py-0.2 rounded-full bg-muted text-muted-foreground">
                {summary.upcoming}
              </span>
            )}
          </button>

          {/* Completed */}
          <button
            type="button"
            onClick={() => setViewTab("completed")}
            className={`px-3.5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
              activeTab === "completed"
                ? "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900 shadow-sm"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            }`}
          >
            <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
            <span>Completed</span>
            {summary.completed > 0 && (
              <span className="text-[11px] font-semibold px-1.5 py-0.2 rounded-full bg-muted text-muted-foreground">
                {summary.completed}
              </span>
            )}
          </button>
        </div>

        {/* Filters & Search */}
        <div className="flex items-center gap-2.5 w-full sm:w-auto">
          {/* Type Filter Select */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[140px] h-9 text-xs">
              <Filter className="size-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Tasks</SelectItem>
              <SelectItem value="FOLLOW_UP">Follow-ups</SelectItem>
              <SelectItem value="CALL">Calls</SelectItem>
              <SelectItem value="MEETING">Meetings</SelectItem>
              <SelectItem value="EMAIL">Emails</SelectItem>
              <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
              <SelectItem value="OTHER">Other</SelectItem>
            </SelectContent>
          </Select>

          {/* Search */}
          <div className="relative w-full sm:w-60">
            <Search className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10 h-9 text-xs"
            />
          </div>
        </div>
      </div>

      {/* Task List Content */}
      <TaskList
        tasks={tasks}
        loading={loading && tasks.length === 0}
        onRefresh={() => {
          fetchTasks();
          fetchSummary();
        }}
        emptyMessage={
          activeTab === "overdue"
            ? "Great job! No overdue tasks."
            : activeTab === "today"
            ? "No tasks due today. Schedule a follow-up to stay ahead!"
            : activeTab === "upcoming"
            ? "No upcoming scheduled tasks."
            : "No completed tasks yet."
        }
      />
      <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />

      {/* Task Create Dialog */}
      <TaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSaved={() => {
          fetchTasks();
          fetchSummary();
        }}
      />
    </div>
  );
}
